import { Icon } from "@circle-react-shared/Icon";
import { CommentHierarchy } from "@circle-react/components/CommentsV3/CommentHierarchy";
import { ALL_COMMENTS_ANCHOR } from "@circle-react/components/CommentsV3/constants";
import { useCommentFromLocationHash } from "@circle-react/components/CommentsV3/useCommentFromLocationHash";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { toLocaleString } from "@circle-react/helpers/number";
import { isEventPost } from "@circle-react/helpers/postHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useCallback, useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { CommentsList } from "@/react/components/CommentsV3/CommentsList";
import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";
import {
  useIsPostModalActive,
  useReplaceLocationState,
} from "@/react/hooks/usePostModalNavigation";

import { ConversationSummary } from "../ConversationSummary";
import { CommentsClosedBanner } from "./CommentsClosedBanner";
import { GatedComments } from "./GatedComments";
import { LeaveCommentButton } from "./LeaveCommentButton";

const ALL_COMMENTS_HASH = ALL_COMMENTS_ANCHOR.replace(/^#/, "");

export const CommentsSection = ({
  autofocusReplyForm,
  commentsFormRef,
  className = "",
  scrollableTarget,
}: {
  autofocusReplyForm?: boolean;
  commentsFormRef: React.RefObject<unknown>;
  className?: string;
  scrollableTarget?: React.RefObject<HTMLElement | null>;
}) => {
  const {
    post,
    showGatedCommentSectionAtBottom: shouldShowGatedCommentSectionAtBottom,
    showGatedCommentSectionAtTop: shouldShowGatedCommentSectionAtTop,
    shouldShowLeaveCommentButton,
    shouldHideCommentsListForSpace,
    refetchPostDetails,
    updatePostDetailsQueryCache,
  } = useCurrentPostHelpers();
  const { commentId } = useCommentFromLocationHash();
  const [linkedCommentId, setLinkedCommentId] = useState(commentId);
  const [showCommentHierarchy, setShowCommentHierarchy] = useState(
    commentId && post.comments_count > 4,
  );
  const isPostModalActive = useIsPostModalActive();

  useEffect(() => {
    if (post?.comments_count) {
      setShowCommentHierarchy(commentId && post.comments_count > 4);
    }
  }, [commentId, post]);

  const { data } = useCurrentSpaceContext();
  const space = data as NonNullable<typeof data>;

  const { isUsedInMinimalTipTapViewModal } = useCurrentPostContext();
  const { is_comments_closed: isCommentsClosed } = post;
  const replaceLocationState = useReplaceLocationState();
  const onChange = () => {
    refetchPostDetails && refetchPostDetails();
  };

  const incrementCommentsCount = () => {
    updatePostDetailsQueryCache &&
      updatePostDetailsQueryCache({
        comments_count: post.comments_count + 1,
      });
  };
  const navigateToAllComments = useCallback(() => {
    setShowCommentHierarchy(false);
    setLinkedCommentId(null);
    replaceLocationState(
      isPostModalActive ? { __commentsAllView: Date.now() } : {}, // TanStack skips mask-only hash updates unless state changes
      { hash: ALL_COMMENTS_HASH },
    );
  }, [isPostModalActive, replaceLocationState]);

  const triggerOnChangeAndNavigateToAllComments = () => {
    onChange();
    navigateToAllComments();
  };

  const handleShowAllCommentsClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();
    navigateToAllComments();
  };

  const shouldHideCommentsList = shouldHideCommentsListForSpace({ space });

  useEffect(() => {
    setLinkedCommentId(commentId);
  }, [commentId]);

  const commentsSectionRef = useRef<HTMLDivElement>(null);

  const shouldShowGatedComments = !(
    shouldShowLeaveCommentButton || isEventPost(post)
  );

  return (
    <>
      {!shouldHideCommentsList && <ConversationSummary post={post} />}
      <div
        ref={commentsSectionRef}
        className={classNames(
          anchorClassNames("post__comments trix-v2"),
          {
            "transition-[margin] duration-150 ease-in-out":
              isUsedInMinimalTipTapViewModal && !isCommentsClosed,
          },
          className,
        )}
        id="all_comments"
        data-post-id={post.id}
      >
        {isCommentsClosed && <CommentsClosedBanner className="m-4" />}
        {shouldShowLeaveCommentButton && <LeaveCommentButton post={post} />}
        {shouldShowGatedComments && shouldShowGatedCommentSectionAtTop && (
          <GatedComments post={post} space={space} />
        )}
        {!shouldHideCommentsList && showCommentHierarchy && linkedCommentId && (
          <>
            <div className="px-2.5 py-4 md:p-6">
              <a
                className="focus-visible:outline-secondary border-hover bg-primary text-dark hover:text-darkest focus-visible:text-darkest inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold leading-normal transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                href="#all_comments"
                onClick={handleShowAllCommentsClick}
              >
                <Icon type="arrow-solid-top-left" />
                {t("post.comments_section.show_all_count_comments", {
                  count: post.comments_count,
                  formatted_count: toLocaleString(post.comments_count),
                })}
              </a>
            </div>
            <CommentHierarchy
              space={space}
              post={post}
              id={linkedCommentId}
              showReplyForm={!isCommentsClosed}
              autofocusReplyForm={autofocusReplyForm}
              onCommentDelete={triggerOnChangeAndNavigateToAllComments}
              onReplyCreate={incrementCommentsCount}
              onReplyDelete={onChange}
            />
          </>
        )}
        {!shouldHideCommentsList && !showCommentHierarchy && (
          <CommentsList
            space={space}
            post={post}
            showReplyForm={!isCommentsClosed}
            autofocusReplyForm={autofocusReplyForm}
            formRef={commentsFormRef}
            onCommentCreate={incrementCommentsCount}
            onCommentDelete={onChange}
            scrollableTarget={scrollableTarget}
            commentsSectionRef={commentsSectionRef}
          />
        )}
        {shouldShowGatedComments && shouldShowGatedCommentSectionAtBottom && (
          <GatedComments post={post} space={space} />
        )}
      </div>
    </>
  );
};
